import React from 'react'
import { v1 as uuidV1 } from 'uuid'
import { FormInput } from 'semantic-ui-react'
import { Field, WrappedFieldProps } from 'redux-form'
import { LocaleContext } from '@cbgms/base/locale/LocaleContext'
import { ReduxFieldProps, SemanticFieldProps, mapErrorProps } from './SemanticField'
import { useMaskedValue } from '@carsys/form/src/utils/useMaskedValue'
import { StrippedInputProps } from './Input'
import { useRegisterTab } from './Tabs/TabItem'

export type ReduxInputProps = SemanticFieldProps<StrippedInputProps>
export type InnerFieldProps = ReduxFieldProps<StrippedInputProps>

const toFloat = (input: string) => {
  if (input === '') {
    return null
  }
  return parseFloat(input.toString().replace(',', '.'))
}

const Component = (props: WrappedFieldProps & StrippedInputProps) => {
  const { input, meta, label, ...rest } = props

  const { formatter } = React.useContext(LocaleContext)
  const [originalValue, setOriginalValue] = React.useState(input.value)

  const changeHandler = (e: React.FocusEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    if (/^-?(\d+)?(\.|\,)?(\d{1,2})?$/.test(value) || value === '') {
      setOriginalValue(value)

      input.onChange(toFloat(value) || 0)
    }
  }

  const handleFocus = () => {
    setOriginalValue(parseFloat(originalValue) ? originalValue : '')
  }

  const blurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value

    input.onBlur(toFloat(value) || 0)
  }

  const [value, events, isEditing] = useMaskedValue(
    input.value,
    value => {
      if (!isEditing) {
        setOriginalValue(value)
      }
      return formatter.format(toFloat(value)!)
    },
    { onChange: changeHandler, onBlur: blurHandler, onFocus: () => handleFocus() }
  )

  const name = input.name && `${uuidV1()}-${input.name}` // use randomized input name to prevent autocompletion

  return (
    <FormInput
      {...input}
      {...events}
      value={isEditing ? originalValue : value}
      {...rest}
      name={name}
      autoComplete='none'
      {...mapErrorProps(meta, label)}
    />
  )
}

const CurrencyInput: React.FC<ReduxInputProps> = props => {
  useRegisterTab(props.name)

  return <Field component={Component} {...props} />
}

CurrencyInput.displayName = 'CurrencyInput'

export default React.memo(CurrencyInput)
